import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CreditCard, Landmark, Banknote } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";
const lbl = "text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5";
const EMPTY = { name: "", type: "iyzico", mode: "sandbox", merchant_id: "", api_key: "", secret_key: "", is_active: false, note: "" };
const TYPE_LBL = { iyzico: "iyzico", paytr: "PayTR", banka: "Banka Sanal POS", diger: "Diğer" };

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [site, setSite] = useState(null);

  const load = () => api.get("/admin/payment-providers").then(({ data }) => setItems(data)).catch(() => toast.error("Sağlayıcılar yüklenemedi"));
  useEffect(() => { load(); api.get("/admin/settings").then(({ data }) => setSite(data)).catch(() => {}); }, []);

  const offline = site?.offline_payments || {};
  const toggleOffline = async (key) => {
    const next = { ...(site || {}), offline_payments: { ...offline, [key]: !offline[key] } };
    setSite(next);
    try {
      await api.put("/admin/settings", { ...next, shipping_fee: Number(next.shipping_fee ?? 49.9), free_shipping_limit: Number(next.free_shipping_limit ?? 750) });
      toast.success("Ödeme yöntemi güncellendi");
    } catch (err) { toast.error(errText(err) || "Güncellenemedi"); }
  };

  const OFFLINE = [
    { key: "havale", label: "Havale / EFT", desc: "Sipariş sonrası banka/IBAN bilgileri müşteriye gösterilir. Banka bilgileri Ayarlar bölümünden düzenlenir.", Icon: Landmark },
    { key: "kapida", label: "Kapıda Ödeme", desc: "Müşteri teslimatta nakit veya kart ile öder. Sipariş 'Onay Bekliyor' statüsünde açılır.", Icon: Banknote },
  ];

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/payment-providers/${editId}`, form);
      else await api.post("/admin/payment-providers", form);
      toast.success("Ödeme sağlayıcısı kaydedildi");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (err) { toast.error(errText(err) || "Kaydedilemedi"); }
  };

  const del = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    try { await api.delete(`/admin/payment-providers/${p.provider_id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errText(err) || "Silinemedi"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/payment-providers/${p.provider_id}`, { name: p.name, type: p.type, mode: p.mode, merchant_id: p.merchant_id || "", api_key: p.api_key || "", secret_key: p.secret_key || "", is_active: !p.is_active, note: p.note || "" });
      toast.success(!p.is_active ? "Etkinleştirildi — ödeme sayfasında görünecek" : "Pasifleştirildi");
      load();
    } catch { toast.error("Güncellenemedi"); }
  };

  return (
    <div data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Ödeme Yöntemleri</h1>
          <p className="text-stone-500 text-sm mt-1">Sanal POS sağlayıcıları (iyzico, PayTR, banka sanal POS) ekleyin, düzenleyin, etkinleştirin. Etkin sağlayıcılar ödeme sayfasında seçenek olarak görünür.</p>
        </div>
        <button data-testid="add-provider-button" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Sağlayıcı</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {items.map((p) => (
          <div key={p.provider_id} data-testid={`provider-card-${p.provider_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.is_active ? "bg-forest/10 text-forest" : "bg-stone-100 text-stone-400"}`}><CreditCard className="w-5 h-5" /></div>
                <div>
                  <p className="font-semibold text-stone-900">{p.name}</p>
                  <p className="text-xs text-stone-400">{TYPE_LBL[p.type] || p.type} · {p.mode === "live" ? "Canlı" : "Test"}</p>
                </div>
              </div>
              <span data-testid={`provider-status-${p.provider_id}`} className={`text-[10px] font-bold px-2 py-1 rounded-lg ${p.is_active ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>{p.is_active ? "AKTİF" : "PASİF"}</span>
            </div>
            {p.note && <p className="text-xs text-stone-500">{p.note}</p>}
            <p className="text-[11px] text-stone-400 font-mono">Merchant: {p.merchant_id || "-"} · API Key: {p.api_key ? "••••••" : "-"}</p>
            <div className="flex gap-2 pt-1">
              <button data-testid={`provider-toggle-${p.provider_id}`} onClick={() => toggleActive(p)} className={`flex-1 text-xs font-semibold py-2 rounded-xl transition-colors ${p.is_active ? "border border-stone-300 text-stone-600 hover:bg-stone-50" : "bg-forest hover:bg-forest-dark text-white"}`}>{p.is_active ? "Pasifleştir" : "Etkinleştir"}</button>
              <button data-testid={`provider-edit-${p.provider_id}`} onClick={() => { setForm({ name: p.name, type: p.type, mode: p.mode, merchant_id: p.merchant_id || "", api_key: p.api_key || "", secret_key: p.secret_key || "", is_active: !!p.is_active, note: p.note || "" }); setEditId(p.provider_id); setOpen(true); }} className="p-2 rounded-lg hover:bg-cream-dark text-stone-600"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`provider-delete-${p.provider_id}`} onClick={() => del(p)} className="p-2 rounded-lg hover:bg-red-50 text-terra"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-stone-400 col-span-full py-10 text-center bg-white rounded-2xl border border-stone-200/80">Henüz sağlayıcı eklenmedi. iyzico, PayTR veya banka sanal POS bilgilerinizi ekleyin.</p>}
      </div>

      <h2 className="font-serif text-xl text-forest mt-8">Çevrimdışı Ödeme Yöntemleri</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {OFFLINE.map((m) => {
          const active = offline[m.key] !== false;
          return (
            <div key={m.key} data-testid={`offline-card-${m.key}`} className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-forest/10 text-forest" : "bg-stone-100 text-stone-400"}`}><m.Icon className="w-5 h-5" /></div>
                  <p className="font-semibold text-stone-900">{m.label}</p>
                </div>
                <span data-testid={`offline-status-${m.key}`} className={`text-[10px] font-bold px-2 py-1 rounded-lg ${active ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>{active ? "AKTİF" : "PASİF"}</span>
              </div>
              <p className="text-xs text-stone-500">{m.desc}</p>
              <button data-testid={`offline-toggle-${m.key}`} onClick={() => toggleOffline(m.key)} disabled={!site} className={`w-full text-xs font-semibold py-2 rounded-xl transition-colors disabled:opacity-50 ${active ? "border border-stone-300 text-stone-600 hover:bg-stone-50" : "bg-forest hover:bg-forest-dark text-white"}`}>{active ? "Pasifleştir" : "Etkinleştir"}</button>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Sağlayıcı Düzenle" : "Yeni Ödeme Sağlayıcısı"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div><label className={lbl}>Sağlayıcı Adı *</label><input data-testid="provider-name-input" required className={inputCls} placeholder="örn: iyzico Ana Hesap, Ziraat Sanal POS" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Tür</label>
                <select data-testid="provider-type-select" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="iyzico">iyzico</option>
                  <option value="paytr">PayTR</option>
                  <option value="banka">Banka Sanal POS</option>
                  <option value="diger">Diğer</option>
                </select></div>
              <div><label className={lbl}>Mod</label>
                <select data-testid="provider-mode-select" className={inputCls} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="live">Canlı (Production)</option>
                </select></div>
            </div>
            <div><label className={lbl}>Merchant ID / Üye İşyeri No</label><input data-testid="provider-merchant-input" className={`${inputCls} font-mono`} value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} autoComplete="off" /></div>
            <div><label className={lbl}>API Key</label><input data-testid="provider-apikey-input" className={`${inputCls} font-mono`} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} autoComplete="off" /></div>
            <div><label className={lbl}>Secret Key</label><input data-testid="provider-secret-input" type="password" className={`${inputCls} font-mono`} value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} autoComplete="new-password" /></div>
            <div><label className={lbl}>Not</label><input className={inputCls} placeholder="Opsiyonel açıklama" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm text-stone-700"><input data-testid="provider-active-check" type="checkbox" className="w-4 h-4 accent-forest" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Etkin — ödeme sayfasında seçenek olarak göster</label>
            {form.type !== "iyzico" && <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2.5">Not: {TYPE_LBL[form.type]} için online tahsilat akışı ayrıca entegre edilecektir; sağlayıcı kaydı ve ödeme sayfasında gösterimi şimdiden aktiftir.</p>}
            <button data-testid="provider-save-button" className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Kaydet</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
